/**
 * Enhanced Certificate Processing Pipeline
 * Integrates Gemini AI OCR with Supabase Database Storage
 */

import { geminiOCR } from './geminiOCR';
import { supabaseClient } from './supabaseClient';
import { anchorHash } from './anchorClient';
import { uploadFileToPinata } from './pinataClient';

// Lightweight hashing using Web Crypto API; expects a File or Blob
export async function sha256HexOfFile(file) {
  const arrayBuffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Upload to Pinata via plain client (no React hooks)
async function pinataUpload(file, institution, ocrData) {
  return uploadFileToPinata(file, institution, ocrData);
}

/**
 * Enhanced certificate processing with Gemini AI and Supabase integration
 */
export async function processCertificateFile({ 
  file, 
  institution, 
  certificateType = 'Degree',
  useGeminiOCR = true 
}) {
  if (!file) throw new Error('Missing file');
  if (!institution) throw new Error('Missing institution');

  console.log('🚀 Starting enhanced certificate processing pipeline...');

  try {
    // 1) Generate file hash
    console.log('📊 Generating file hash...');
    const fileHash = await sha256HexOfFile(file);
    
    // 2) Check if certificate already exists in database
    console.log('🔍 Checking if certificate exists in database...');
    const existingCertificate = await supabaseClient.findCertificateByHash(fileHash);
    if (existingCertificate) {
      console.log('📋 Certificate already exists in database');
      return {
        fileHash,
        existingCertificate,
        isExisting: true,
        message: 'Certificate already processed and stored in database'
      };
    }

    // 3) Enhanced OCR processing (images only; PDFs are skipped for now)
    let ocrData = null;
    let extractionMethod = 'none';
    
    if (file.type && file.type.startsWith('image/')) {
      console.log('📄 Processing OCR with Gemini AI...');
      
      try {
        ocrData = await geminiOCR.extractCertificateData(file);
        extractionMethod = 'gemini_ai';
        console.log('✅ Gemini OCR successful:', ocrData);
      } catch (geminiError) {
        console.error('❌ Gemini OCR failed:', geminiError.message);
        ocrData = geminiOCR.createFallbackOCR(file.name, '');
        extractionMethod = 'manual_fallback';
      }
    } else {
      console.log('📄 Non-image file, skipping OCR');
      ocrData = geminiOCR.createFallbackOCR(file.name, '');
      extractionMethod = 'non_image';
    }

    // 4) Upload to IPFS (Pinata) with enhanced OCR metadata
    console.log('📤 Uploading to IPFS...');
    const uploadResult = await pinataUpload(file, institution, ocrData);

    // 5) Anchor hash on blockchain (stub)
    console.log('⚓ Anchoring on blockchain...');
    const anchor = await anchorHash(fileHash);

    // 6) Prepare enhanced database record
    const databaseRecord = {
      institution: ocrData?.institution || institution,
      institution_id: ocrData?.institution_id || generateInstitutionId(institution),
      student_id: ocrData?.student_id,
      student_name: ocrData?.student_name,
      roll_number: ocrData?.roll_number,
      certificate_type: ocrData?.certificate_type || certificateType,
      file_name: file.name,
      file_hash: fileHash,
      cid: uploadResult.IpfsHash,
      ipfs_url: `https://gateway.pinata.cloud/ipfs/${uploadResult.IpfsHash}`,
      anchor_tx: anchor.txId,
      anchor_url: anchor.url,
      ocr_data: {
        ...ocrData,
        extraction_method: extractionMethod,
        processing_timestamp: new Date().toISOString(),
        file_size: file.size,
        file_type: file.type
      },
      marks: ocrData?.marks,
      program: ocrData?.program,
      batch: ocrData?.batch
    };

    // 7) Save to Supabase database
    console.log('💾 Saving to Supabase database...');
    let supabaseRecord = null;
    try {
      supabaseRecord = await supabaseClient.saveCertificate(databaseRecord);
      console.log('✅ Certificate saved to database successfully');
    } catch (dbError) {
      console.error('❌ Database save failed:', dbError.message);
      // Continue processing even if database save fails
    }

    // 8) Return comprehensive processing result
    const result = {
      fileHash,
      uploadResult,
      anchor,
      ocrData,
      extractionMethod,
      databaseRecord,
      supabaseRecord,
      isExisting: false,
      message: 'Certificate processed successfully',
      pipeline_summary: {
        ocr_method: extractionMethod,
        ipfs_stored: !!uploadResult.IpfsHash,
        blockchain_anchored: !!anchor.txId,
        database_saved: !!supabaseRecord,
        processing_time: new Date().toISOString()
      }
    };

    console.log('🎉 Certificate processing pipeline completed successfully');
    return result;

  } catch (error) {
    console.error('❌ Certificate processing pipeline failed:', error);
    throw new Error(`Certificate processing failed: ${error.message}`);
  }
}

/**
 * Batch process multiple certificates
 */
export async function batchProcessCertificates(files, institution, options = {}) {
  const results = [];
  const errors = [];

  console.log(`🚀 Starting batch processing of ${files.length} certificates...`);

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    console.log(`📄 Processing file ${i + 1}/${files.length}: ${file.name}`);

    try {
      const result = await processCertificateFile({
        file,
        institution,
        ...options
      });
      results.push(result);
    } catch (error) {
      console.error(`❌ Failed to process ${file.name}:`, error);
      errors.push({
        fileName: file.name,
        error: error.message
      });
    }
  }

  console.log(`✅ Batch processing completed: ${results.length} successful, ${errors.length} failed`);

  return {
    successful: results,
    failed: errors,
    summary: {
      total: files.length,
      successful: results.length,
      failed: errors.length
    }
  };
}

/**
 * Legacy parseTextToFields for Google Vision fallback
 */
export function parseTextToFields(text) {
  const data = {
    student_name: '',
    roll_number: '',
    certificate_type: '',
    issue_date: '',
    marks: '',
    institution: '',
    program: '',
    batch: '',
    raw_text: text || '',
    extraction_metadata: {
      method: 'google_vision_legacy',
      timestamp: new Date().toISOString(),
      confidence: 'medium'
    }
  };

  if (!text) return data;

  // Student name patterns
  const namePatterns = [
    /(?:Name|Student Name|Full Name|Name of Student)[\s:]+([A-Za-z\s]{3,50})/i,
    /This is to certify that\s+([A-Za-z\s]{3,50})/i,
    /(?:Mr\.|Ms\.|Miss)\s+([A-Za-z\s]{3,50})/i,
    /^([A-Za-z\s]{3,50})\s+(?:Has successfully|has successfully|Has completed|has completed)/i,
    /(?:hereby certifies that|certifies that)\s+([A-Za-z\s]{3,50})/i,
  ];
  for (const p of namePatterns) { 
    const m = text.match(p); 
    if (m?.[1]) { 
      data.student_name = m[1].trim(); 
      break; 
    } 
  }

  // Roll number patterns
  const rollPatterns = [
    /(?:Roll|Registration|Student ID|Reg\.?\s*No\.?|Roll No\.?)\s*[:\-]?\s*([A-Z0-9\/-]{3,20})/i,
    /(?:Serial Number|Certificate ID|Cert ID|ID)\s*[:\-]?\s*([A-Z0-9]{6,25})/i,
    /\b([A-Z]{2,4}\d{10,20})\b/i,
  ];
  for (const p of rollPatterns) { 
    const m = text.match(p); 
    if (m?.[1]) { 
      data.roll_number = m[1].trim(); 
      break; 
    } 
  }

  // Certificate type patterns
  const certPatterns = [
    /(Bachelor of [A-Za-z\s]+)/i,
    /(Master of [A-Za-z\s]+)/i,
    /(Diploma in [A-Za-z\s]+)/i,
    /(Certified [A-Za-z\s]+)/i,
    /([A-Za-z\s]+ Certification)/i,
    /(Degree Certificate)/i,
    /(Mark Sheet)/i,
    /(Transcript)/i
  ];
  for (const p of certPatterns) { 
    const m = text.match(p); 
    if (m?.[1]) { 
      data.certificate_type = m[1].trim(); 
      break; 
    } 
  }

  // Date patterns
  const datePatterns = [
    /(?:Date|Issued|Issue Date|Date of Issue)\s*[:\-]?\s*(\d{1,2}[\/.\-]\d{1,2}[\/.\-]\d{2,4})/i,
    /(\d{1,2}(?:st|nd|rd|th)?\s+[A-Za-z]+\s+\d{4})/i,
  ];
  for (const p of datePatterns) { 
    const m = text.match(p); 
    if (m?.[1]) { 
      data.issue_date = m[1].trim(); 
      break; 
    } 
  }

  // Marks patterns
  const marksPatterns = [
    /(?:Marks|Grade|CGPA|Percentage|Score)\s*[:\-]?\s*([0-9.]+\s*(?:%|CGPA|\/100|out of)?)/i,
    /(?:secured|with)\s+([0-9.]+\s*(?:%|CGPA|marks))/i,
  ];
  for (const p of marksPatterns) { 
    const m = text.match(p); 
    if (m?.[1]) { 
      data.marks = m[1].trim(); 
      break; 
    } 
  }

  return data;
}

/**
 * Generate institution ID from name
 */
function generateInstitutionId(institutionName) {
  if (!institutionName) return null;
  
  return institutionName
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '_')
    .replace(/university|college|institute|school/g, '')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .substring(0, 20);
}

/**
 * Verify certificate using database lookup
 */
export async function verifyCertificateFromDatabase(fileHash) {
  try {
    console.log('🔍 Verifying certificate in database:', fileHash);
    
    const certificate = await supabaseClient.findCertificateByHash(fileHash);
    
    if (certificate) {
      return {
        verified: true,
        certificate,
        message: 'Certificate found and verified in database',
        verification_method: 'database_lookup'
      };
    } else {
      return {
        verified: false,
        message: 'Certificate not found in database',
        verification_method: 'database_lookup'
      };
    }
  } catch (error) {
    console.error('❌ Database verification failed:', error);
    return {
      verified: false,
      error: error.message,
      message: 'Database verification failed'
    };
  }
}

// Export legacy function for backward compatibility
export { processCertificateFile as legacyProcessCertificate };


